import { createContext, use, useCallback, useMemo, useState } from 'react'
import type {
  RunClassTestCase,
  RunTestCase,
  TestCasesState,
} from '@/lib/types/test-cases'

// Re-export for backwards compatibility
export type { RunClassTestCase, TestCasesState }

interface TestCasesContextValue {
  /** Current test cases state (discriminated union with type) */
  state: TestCasesState | null

  /** Set function-based test cases */
  setFunctionTestCases: (cases: RunTestCase[]) => void

  /** Set class-based test cases */
  setClassTestCases: (cases: RunClassTestCase[]) => void

  /** Set raw test scripts */
  setRawTestScripts: (scripts: string[]) => void
}

const TestCasesContext = createContext<TestCasesContextValue | null>(null)

export function TestCasesProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<TestCasesState | null>(null)

  const setFunctionTestCases = useCallback((cases: RunTestCase[]) => {
    setState({ type: 'function', cases })
  }, [])

  const setClassTestCases = useCallback((cases: RunClassTestCase[]) => {
    setState({ type: 'class', cases })
  }, [])

  const setRawTestScripts = useCallback((scripts: string[]) => {
    setState({ type: 'raw', scripts })
  }, [])

  const value = useMemo(
    () => ({
      state,
      setFunctionTestCases,
      setClassTestCases,
      setRawTestScripts,
    }),
    [state, setFunctionTestCases, setClassTestCases, setRawTestScripts],
  )

  return <TestCasesContext value={value}>{children}</TestCasesContext>
}

export function useTestCases() {
  const context = use(TestCasesContext)
  if (!context) {
    throw new Error('useTestCases must be used within a TestCasesProvider')
  }
  return context
}

export function useTestCasesOptional() {
  return use(TestCasesContext)
}
